/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useGSAP } from '@gsap/react';
import { graphql } from 'gatsby';
import gsap from 'gsap';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload } from 'react-icons/fa';
import { ButtonLinkType } from '../../graphql-fragments/ButtonLink';
import { SanityImageType } from '../../graphql-fragments/SanityImage';
import TranscriptIcon from '../../images/transcript.inline.svg';
import { BREAKPOINTS } from '../../styles/breakpoints';
import { BackgroundColorTheme } from '../../templates/Page';
import {
  pushCalendlyButtonClickEvent,
  pushWhatsappButtonClickEvent,
  withDataLayer,
} from '../../tracking/dataLayer';
import { usePersistentState } from '../../utils/hooks';
import { handleCalendlyClick, useRunOnceWhenVisible } from '../../utils/projectUtils';
import { getUrlFromVersatileLink } from '../../utils/sanity';
import { clsx, replaceNewLinesWithBr } from '../../utils/utils';
import Arc from './Arc';
import ButtonLink from './ButtonLink';
import ContactUsButton from './ContactUsButton';
import * as styles from './Hero.module.scss';
import Image from './Image';
import ModuleLayout from './ModuleLayout';
import TextLink from './TextLink';
import Video from './Video';
import VideoTranscriptionModal from './VideoTranscriptionModal';
import WhatsappButton from './WhatsappButton';

export const VIDEO_TIMEOUT_MS = 2000;

export const query = graphql`
  fragment Hero on SanityPageHero {
    title {
      ...LocaleStringFragment
    }
    subtitle {
      ...LocaleStringFragment
    }
    text {
      ...LocaleTextFragment
    }
    withSideVideo
    withHeedMeaning
    videoUrl {
      ...LocaleUrlFragment
    }
    videoTranscription {
      ...LocaleTextFragment
    }
    videoTitle {
      ...LocaleStringFragment
    }
    videoSubtitle {
      ...LocaleStringFragment
    }
    brochure {
      ...LocaleFileFragment
    }
    brochureUrl {
      ...LocaleStringFragment
    }
    backgroundType
    backgroundVideo {
      asset {
        url
      }
    }
    fallbackImage {
      ...SanityImage
    }
    mobileBackgroundImage {
      ...SanityImage
    }
    desktopBackgroundImage {
      ...SanityImage
    }
    textSide
    addContactUsButton
  }
`;

export type HeroType = {
  title: string;
  subtitle?: string;
  text?: string;
} & (
  | {
      backgroundType: 'video';
      backgroundVideo: {
        asset: {
          url: string;
        };
      };
      fallbackImage: SanityImageType;
      mobileBackgroundImage?: never;
      desktopBackgroundImage?: never;
    }
  | {
      backgroundType: 'image';
      backgroundVideo?: never;
      fallbackImage?: never;
      mobileBackgroundImage?: SanityImageType;
      desktopBackgroundImage: SanityImageType;
    }
) &
  (
    | {
        onlyText: true;
        withSideVideo?: never;
        videoUrl?: never;
        videoTitle?: never;
        videoSubtitle?: never;
        videoTranscription?: never;
        brochure?: never;
        brochureUrl?: never;
        addContactUsButton?: never;
        withHeedMeaning?: never;
        textSide?: never;
      }
    | {
        onlyText?: never;
        withSideVideo?: boolean;
        videoUrl?: string;
        videoTitle?: string;
        videoSubtitle?: string;
        videoTranscription?: string;
        brochure?: {
          asset: {
            url: string;
          };
        };
        brochureUrl?: string;
        addContactUsButton?: boolean;
        withHeedMeaning?: boolean;
        textSide: 'left' | 'right';
      }
  );

type HeroProps = HeroType & {
  theme?: BackgroundColorTheme;
  heroRef: React.MutableRefObject<HTMLElement | null>;
  calendlyLink?: string;
  addWhatsappButton?: boolean;
  buttonLink?: ButtonLinkType;
  isFullHeight?: boolean;
  withAnimations?: boolean;
  children?: React.ReactNode;
  className?: string;
};

const Hero = ({
  heroRef,
  textSide,
  title,
  subtitle,
  text,
  buttonLink,
  withSideVideo,
  videoUrl,
  videoTranscription,
  videoTitle,
  videoSubtitle,
  brochureUrl,
  backgroundType,
  mobileBackgroundImage,
  desktopBackgroundImage,
  backgroundVideo,
  fallbackImage,
  calendlyLink,
  addContactUsButton,
  addWhatsappButton,
  withHeedMeaning,
  isFullHeight,
  withAnimations,
  onlyText,
  children,
}: HeroProps): React.ReactElement => {
  const { t } = useTranslation();

  const [isDesktopVideoTranscriptionModalOpen, setIsDesktopVideoTranscriptionModalOpen] =
    useState(false);
  const [isMobileVideoTranscriptionModalOpen, setIsMobileVideoTranscriptionModalOpen] =
    useState(false);

  const [hasLoadedBefore, setHasLoadedBefore] = usePersistentState<boolean>(
    'hasLoadedBefore',
    false,
    'session',
  );
  useGSAP(
    () => {
      const tl = gsap.timeline({});

      tl.fromTo(
        `.${styles.title}`,
        {
          x: -40,
          opacity: 0,
        },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: 'circ.out',
        },
      );

      if (subtitle || text || withHeedMeaning) {
        tl.fromTo(
          `.${styles.subtitle}, .${styles.text}, .${styles.dictionarySection}`,
          {
            x: -60,
            opacity: 0,
          },
          {
            x: 0,
            opacity: 1,

            duration: 1.5,
            ease: 'circ.out',
            stagger: 0.2,
          },

          '<',
        );
      }

      if (buttonLink || addWhatsappButton || addContactUsButton) {
        tl.fromTo(
          `.${styles.buttonsContainer}`,
          {
            x: -40,
            opacity: 0,
          },
          {
            x: 0,
            opacity: 1,
            duration: 0.6,
            ease: 'circ.out',
          },
        );
      }

      // run animation on load only
      if (hasLoadedBefore || !withAnimations) {
        tl.seek('100%');
      } else {
        setHasLoadedBefore(true);
      }
    },
    { scope: heroRef },
  );

  function renderVideo(resolution: 'mobile' | 'desktop') {
    return (
      withSideVideo &&
      (videoUrl || brochureUrl) && (
        <div className={clsx(styles.videoAndLinksContainer, styles[resolution])}>
          {videoUrl && <Video className={styles.video} url={videoUrl} />}

          {(videoTranscription || brochureUrl) && (
            <div className={styles.linksContainer}>
              {videoUrl && videoTranscription && (
                <>
                  <button
                    onClick={() => {
                      if (resolution === 'mobile') {
                        setIsMobileVideoTranscriptionModalOpen(true);
                        setIsDesktopVideoTranscriptionModalOpen(false);
                      }
                      if (resolution === 'desktop') {
                        setIsDesktopVideoTranscriptionModalOpen(true);
                        setIsMobileVideoTranscriptionModalOpen(false);
                      }
                    }}
                    className={styles.link}
                  >
                    {t('hero.button_video_transcript', 'Ver transcrição do vídeo')}{' '}
                    <TranscriptIcon className={styles.linkIcon} />
                  </button>
                  <VideoTranscriptionModal
                    isOpen={
                      (resolution === 'mobile' && isMobileVideoTranscriptionModalOpen) ||
                      (resolution === 'desktop' && isDesktopVideoTranscriptionModalOpen)
                    }
                    title={videoTitle}
                    subtitle={videoSubtitle}
                    text={videoTranscription}
                    onClose={() => {
                      if (resolution === 'mobile') {
                        setIsMobileVideoTranscriptionModalOpen(false);
                      }
                      if (resolution === 'desktop') {
                        setIsDesktopVideoTranscriptionModalOpen(false);
                      }
                    }}
                  />
                </>
              )}
              {brochureUrl && (
                <TextLink to={brochureUrl} className={styles.link} withExternalIcon={false}>
                  {t('hero.button_download_brochure', 'Descarregar Brochura')}
                  <FaDownload className={styles.linkIcon} />
                </TextLink>
              )}
            </div>
          )}
        </div>
      )
    );
  }

  const [videoState, setVideoState] = useState<'pending' | 'playing'>('pending');
  const [videoTimedout, setVideoTimedout] = useState(false);

  const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);

  useRunOnceWhenVisible(videoRef, () => {
    const videoTimeout = setTimeout(() => {
      setVideoTimedout(true);
    }, VIDEO_TIMEOUT_MS);

    if (videoRef) {
      if (!videoRef.paused) {
        setVideoState('playing');
        clearTimeout(videoTimeout);
      }
      videoRef.addEventListener('playing', () => {
        setVideoState('playing');
        clearTimeout(videoTimeout);
      });
    }
  });

  return (
    <ModuleLayout
      moduleRef={heroRef}
      className={clsx(
        styles.container,
        styles.withBackgroundImage,
        isFullHeight && styles.fullHeight,
        onlyText && styles.onlyText,
      )}
      contentClassName={clsx(styles.contentContainer)}
    >
      {backgroundType === 'video' ? (
        <div className={styles.backgroundVideoContainer}>
          <video
            ref={setVideoRef}
            className={clsx(
              styles.backgroundVideo,
              (videoTimedout || videoState !== 'playing') && styles.videoHidden,
            )}
            playsInline
            autoPlay
            muted
            loop
            poster={fallbackImage?.asset.url}
          >
            <source src={backgroundVideo.asset.url} type="video/mp4" />
          </video>
          <img
            src={fallbackImage?.asset.url}
            alt=""
            className={clsx(styles.fallbackImage, videoTimedout && styles.fallbackImageVisible)}
          />
        </div>
      ) : (
        <div className={styles.backgroundImageContainer}>
          <Image
            eagerLoading
            className={styles.backgroundImage}
            image={mobileBackgroundImage || desktopBackgroundImage}
            dimensions={[
              [600, 800],
              [BREAKPOINTS.tabletHorizontal, 800, 850],
              [BREAKPOINTS.laptop, desktopBackgroundImage, 2200, 1240],
            ]}
            cover
          />
          <div className={styles.overlay}></div>
        </div>
      )}
      <div className={clsx(styles.contentWrapper, textSide === 'right' && styles.textRight)}>
        <div className={clsx(styles.textContainer)}>
          {!onlyText && (
            <Arc borderSide={textSide === 'left' ? 'left' : 'right'} className={styles.arc}></Arc>
          )}
          <div className={clsx(styles.textWrapper, !subtitle && styles.narrowText)}>
            <div className={styles.topSpacing}></div>
            <h1 className={clsx(styles.title)}>{replaceNewLinesWithBr(title)}</h1>
            {subtitle && <p className={clsx(styles.subtitle)}>{subtitle}</p>}
            {text && <p className={clsx(styles.text)}>{replaceNewLinesWithBr(text)}</p>}
            {withHeedMeaning && (
              <div className={styles.dictionarySection}>
                <span className={styles.name}>Heed</span>
                <span className={styles.pronunciationAndLabel}>
                  <span className={styles.pronunciation}>[/hêd/]</span>
                  {' ' + t('heroDefinition.name', 'nome')}
                </span>
                <span className={styles.definition}>
                  {t('heroDefinition.definition', 'Atenção cuidadosa.')}
                </span>
              </div>
            )}
            {(buttonLink || addWhatsappButton || addContactUsButton) && (
              <div className={styles.buttonsContainer}>
                {buttonLink && (
                  <div className={styles.buttonWrapper}>
                    <ButtonLink
                      className={clsx(styles.button)}
                      to={getUrlFromVersatileLink(buttonLink)}
                      onClick={() => {
                        if (buttonLink.externalLink?.linkToCalendly && calendlyLink) {
                          handleCalendlyClick(calendlyLink);
                          withDataLayer(pushCalendlyButtonClickEvent({ location: 'hero' }));
                        }
                      }}
                      withCalendarIcon={!!buttonLink.externalLink?.linkToCalendly}
                    >
                      {buttonLink.text}
                    </ButtonLink>
                  </div>
                )}

                {addContactUsButton && (
                  <div className={styles.buttonWrapper}>
                    <ContactUsButton className={styles.button} />
                  </div>
                )}

                {addWhatsappButton && (
                  <div className={styles.buttonWrapper}>
                    <WhatsappButton
                      className={styles.button}
                      onClick={() => {
                        withDataLayer(pushWhatsappButtonClickEvent({ location: 'hero' }));
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {children && <div className={styles.children}>{children}</div>}

            {renderVideo('mobile')}

            {!withSideVideo && brochureUrl && (
              <TextLink to={brochureUrl} className={styles.link} withExternalIcon={false}>
                {t('hero.button_download_brochure', 'Descarregar Brochura')}
                <FaDownload className={styles.linkIcon} />
              </TextLink>
            )}
            <div className={styles.bottomSpacing}></div>
          </div>
        </div>

        {renderVideo('desktop')}
      </div>
    </ModuleLayout>
  );
};

export default Hero;
